import { Modal, Form, Input, Select, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserManagementTableColumns } from '~/app/hooks/user/useUserManagementTableColumns';
import type { ManagersTabPageViewProps } from './ManagersTabPage.types';
import type { CreateOrganizationUserRequestDto } from '@evimo/types/api/user/create-organization-user/create-organization-user.request.dto';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import {
  FilterBarActions,
  FilterBarAddButton,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';

export function ManagersTabPageView({
  organizationId,
  users,
  loading,
  onCreateUser,
  onDeleteUser,
  onRefresh,
}: ManagersTabPageViewProps) {
  const { t } = useTranslation('common');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm<CreateOrganizationUserRequestDto>();
  const { columns } = useUserManagementTableColumns({
    onDelete: onDeleteUser,
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onCreateUser({
        ...values,
        organizationId,
        role: 'ORGANIZATION_MANAGER',
      });
      message.success('Manager created successfully');
      form.resetFields();
      setIsModalOpen(false);
    } catch {
      message.error('Failed to create manager');
    }
  };

  return (
    <div>
      <EvimoListPanel
        title="Managers"
        extra={
          <FilterBarActions>
            <FilterBarRefreshButton onClick={onRefresh} loading={loading} />
            <FilterBarAddButton
              labelKey="buttons.addManager"
              defaultLabel="Add manager"
              onClick={() => setIsModalOpen(true)}
            />
          </FilterBarActions>
        }
      >
        <EvimoTable
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </EvimoListPanel>

      <Modal
        title="Add Manager"
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        okText="Create"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email' },
            ]}
          >
            <Input placeholder="manager@example.com" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please enter password' }]}
          >
            <Input.Password placeholder="Password" />
          </Form.Item>
          <Form.Item
            name="language"
            label="Language"
            rules={[{ required: true, message: 'Please select language' }]}
            initialValue="en"
          >
            <Select>
              <Select.Option value="en">{t('languages.en')}</Select.Option>
              <Select.Option value="fr">{t('languages.fr')}</Select.Option>
              <Select.Option value="de">{t('languages.de')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="+33 6 12 34 56 78" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
